import { useCallback, useEffect, useRef, useState } from 'react';
import { NotificationApi } from '../api';
import { useAuth } from '../context/AuthContext';

const POLL_MS = 30000;

// Short chime via WebAudio so we don't ship an audio file for one sound.
function ring() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    // Same two-note shape as the app's tune.
    [{ f: 1318.51, t: 0 }, { f: 1760, t: 0.14 }].forEach(({ f, t }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = f;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.22, now + t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.45);
    });
    setTimeout(() => ctx.close().catch(() => {}), 900);
  } catch (_) { /* audio blocked — the badge still updates */ }
}

const timeAgo = (d) => {
  const mins = Math.round((Date.now() - new Date(d)) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};

export default function NotificationBell() {
  const { isLoggedIn } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const prevUnread = useRef(null);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { items: list = [], unread: n = 0 } = await NotificationApi.list();
      setItems(list);
      setUnread(n);
      // Ring only on a genuine increase, never on the first load of a session.
      if (prevUnread.current !== null && n > prevUnread.current) ring();
      prevUnread.current = n;
    } catch (_) { /* offline — retry next tick */ }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setItems([]);
      setUnread(0);
      prevUnread.current = null;
      return undefined;
    }
    load();
    const id = setInterval(load, POLL_MS);
    // Catch up when the tab regains focus.
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [isLoggedIn, load]);

  // Close when clicking anywhere else.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try {
        await NotificationApi.markRead();
        setUnread(0);
        prevUnread.current = 0;
        setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      } catch (_) { /* keep the badge if it didn't stick */ }
    }
  };

  if (!isLoggedIn) return null;

  return (
    <div className="util-icon nb-wrap" ref={wrapRef}>
      <button className="nb-btn" onClick={toggle} aria-label="Notifications">
        <i className={`fa-bell ${unread > 0 ? 'fas nb-live' : 'far'}`} />
        {unread > 0 ? <span className="badge">{unread > 9 ? '9+' : unread}</span> : null}
        <span className="util-label">Alerts</span>
      </button>

      {open ? (
        <div className="nb-panel">
          <div className="nb-head">Notifications</div>
          {items.length ? (
            <ul className="nb-list">
              {items.map((n) => (
                <li key={n._id} className={n.read ? '' : 'is-unread'}>
                  {n.image ? <img src={n.image} alt="" /> : <i className="fas fa-bell" />}
                  <div>
                    <strong>{n.title}</strong>
                    {n.body ? <p>{n.body}</p> : null}
                    <span className="nb-time">{timeAgo(n.createdAt)}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="nb-empty">No notifications yet</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
